import { type CellContext } from "@tanstack/react-table";
import cx from "classnames";
import { format, formatDistanceToNowStrict, isValid, parseISO } from "date-fns";
import { type FC, type ReactNode, useEffect, useRef, useState } from "react";

import { BasicTooltip } from "../../Tooltip";
import { type DataGridColumnDef } from "../useColumnState";
import { MaybeWrapInColumnReadonlyPopover } from "./MaybeWrapInColumnReadonlyPopover";
import { TableCellValueCheckbox } from "./TableCellValueCheckbox";
import { TableCellValueDate } from "./TableCellValueDate";
import { TableCellValueEmojiReactions } from "./TableCellValueEmojiReactions";
import { TableCellValueInputNumber } from "./TableCellValueInputNumber";
import { TableCellValueInputText } from "./TableCellValueInputText";
import { TableCellValueMultilineText } from "./TableCellValueMultilineText";
import { TableCellValuePerson } from "./TableCellValuePerson";
import { TableCellValueProgressBar } from "./TableCellValueProgressBar";
import { TableCellValueReactions } from "./TableCellValueReactions";
import { TableCellValueTable } from "./TableCellValueTable";
import { TableCellValueTag } from "./TableCellValueTag";
import { TableCellValueURL } from "./TableCellValueURL";
import { type TableCellValueCommonProps } from "./types";
import { markHighlights } from "./utils";

const formatTime = (d) => <>{format(d, "h:mm aa")}</>;

const MARKABLE_TYPES = ["Text", "Tag", "Email", "URL", "Number"];

export type DefaultCellRendererProps = CellContext<unknown, unknown> & {
	groupIndices?: number[];
};

export const TableCellValue: FC<DefaultCellRendererProps> = (props) => {
	const cellValueRef = useRef<any>(null);
	const { meta } = props.table.options;

	const groupIndices = props.groupIndices ?? [];

	const rowIndex = groupIndices.length > 0 ? groupIndices : props.row.index;

	const updateData = meta?.updateData;
	const onChange =
		updateData !== undefined
			? (value: unknown) => {
					updateData(rowIndex, columnID, value);
			  }
			: undefined;

	// We need to keep and update the state of the cell normally
	const initialValue = props.getValue();
	const columnDef = props.column.columnDef as DataGridColumnDef<unknown, unknown>;
	const [value, setValue] = useState<any>(initialValue);
	const defaultDataValue = meta?.defaultDataValue;
	const columnID = props.column.id;
	const defaultValue = defaultDataValue?.[props.row.index]?.[columnID];
	const columnReadonly = columnDef?.readonly || false;
	const header = columnDef.header?.toString();

	// If the initialValue is changed externally and different than value, sync it up with value
	useEffect(() => {
		if (initialValue !== value) {
			setValue(initialValue);
		}
	}, [initialValue]);

	const isEditable =
		typeof onChange === "function" && meta?.tableEditable !== false && !columnReadonly;

	// const updateColumn = meta?.updateColumn;
	// const isColumnEditable = typeof updateColumn === "function";

	const highlightIndices = undefined;
	const renderAsRowDetail = props.renderAsRowDetail;
	const commonProps: TableCellValueCommonProps = {
		value,
		defaultValue,
		onChange,
		isEditable,
		renderAsRowDetail,
		header,
	};

	const TextInput = (
		<TableCellValueInputText
			{...commonProps}
			highlightIndices={highlightIndices}
			cellValueRef={cellValueRef}
		/>
	);

	let valueToRender: ReactNode | string = (
		<>{typeof value === "string" ? value : safeStringify(value)}</>
	);

	let typeMismatchError = false;
	switch (columnDef.fieldType) {
		case "Text":
			typeMismatchError = value !== undefined && value !== null && typeof value !== "string";
			valueToRender = TextInput;
			break;
		case "Auto":
			valueToRender = TextInput;
			break;
		case "Tag":
			typeMismatchError =
				value !== undefined &&
				value !== null &&
				typeof value !== "string" &&
				!Array.isArray(value);
			valueToRender = (
				<TableCellValueTag
					{...commonProps}
					highlightIndices={highlightIndices}
					columnTypeInfo={columnDef.fieldTypeInfo}
					renderAsRowDetail={renderAsRowDetail}
				/>
			);
			break;
		case "Date":
			typeMismatchError =
				value !== undefined &&
				value !== null &&
				!(value instanceof Date && isValid(value)) &&
				!isValid(parseISO(value));
			valueToRender = <TableCellValueDate type="Date" label={header} {...commonProps} />;
			break;
		case "Time": {
			if (value instanceof Date) {
				valueToRender = formatTime(value);
			}

			const parsed = parseISO(value);
			if (isNaN(parsed.getTime())) {
				valueToRender = <>-</>;
			}
			valueToRender = formatTime(parsed);
			break;
		}
		case "Time distance to now": {
			if (value instanceof Date) {
				valueToRender = formatDistanceToNowStrict(value, { addSuffix: true });
			}

			const parsed = parseISO(value);
			if (isNaN(parsed.getTime())) {
				valueToRender = <>-</>;
			}
			valueToRender = formatDistanceToNowStrict(parsed, { addSuffix: true });
			break;
		}
		case "Date time":
			typeMismatchError =
				value !== undefined &&
				value !== null &&
				!(value instanceof Date && isValid(value)) &&
				!isValid(parseISO(value));
			valueToRender = <TableCellValueDate type="Date time" label={header} {...commonProps} />;
			break;
		case "Boolean":
			typeMismatchError = value !== undefined && value !== null && typeof value !== "boolean";
			valueToRender = <TableCellValueCheckbox {...commonProps} label={header} />;
			break;
		case "Email":
			valueToRender = (
				<a href={`mailto:${value}`} target={value}>
					{markHighlights(value, highlightIndices)}
				</a>
			);
			break;
		case "URL":
			valueToRender = (
				<TableCellValueURL
					{...commonProps}
					columnTypeInfo={columnDef.fieldTypeInfo}
					row={props.row}
					updateData={updateData}
					columnID={columnID}
				/>
			);
			break;
		case "Number":
			typeMismatchError = typeof value !== "number" && value !== undefined && value !== null;
			valueToRender = (
				<TableCellValueInputNumber
					{...commonProps}
					highlightIndices={highlightIndices}
					cellValueRef={cellValueRef}
				/>
			);
			break;
		case "Reaction":
			typeMismatchError = !Array.isArray(value) && value !== undefined && value !== null;
			valueToRender = (
				<TableCellValueReactions {...commonProps} currentUser={meta?.currentUser} />
			);
			break;
		case "Multiline text":
			valueToRender = (
				<TableCellValueMultilineText
					{...commonProps}
					table={props.table}
					row={props.row}
					header={header}
					columnID={columnID}
				/>
			);
			break;
		case "Table":
			valueToRender = (
				<TableCellValueTable
					{...commonProps}
					table={props.table}
					row={props.row}
					header={header}
					columnId={columnID}
				/>
			);
			break;
		case "Person":
			typeMismatchError =
				!(typeof value === "object" && typeof value.id === "string") &&
				value !== undefined &&
				value !== null;
			valueToRender = (
				<TableCellValuePerson
					{...commonProps}
					currentUser={meta?.currentUser}
					users={meta?.users ?? []}
				/>
			);
			break;
		case "Progress bar":
			typeMismatchError = typeof value !== "number" && value !== undefined && value !== null;
			valueToRender = (
				<TableCellValueProgressBar {...commonProps} label={header ?? "Progress"} />
			);
			break;
		case "Emoji reactions":
			valueToRender = (
				<TableCellValueEmojiReactions {...commonProps} currentUser={meta?.currentUser} />
			);
			break;
	}

	const renderWrapper = (
		<div
			className={cx(
				"flex",
				"items-center",
				"w-full",
				"h-full",
				"text-primary",
				!MARKABLE_TYPES.includes(columnDef.fieldType || "Auto") &&
					highlightIndices !== undefined &&
					"bg-warning-light"
			)}
		>
			{valueToRender}
		</div>
	);

	const rendered = (
		<div className="relative">
			<div
				className={cx(
					"overflow-hidden",
					"flex",
					"items-center",
					"space-x-2",
					"px-2",
					"py-0.5",
					"min-h-[2.5rem]",
					"transition-none", // Click should INSTANTLY change the border.
					// "border",
					// "border-transparent",

					"hover:outline",
					"hover:outline-1",
					"hover:outline-brand-light",
					"mt-px",

					"focus-within:outline",
					"focus-within:outline-2",
					"hover:focus-within:outline-2",
					"focus-within:shadow-md",
					"focus-within:outline-brand",
					"hover:focus-within:outline-brand",
					"focus-within:-outline-offset-1",

					"focus-within:z-app-toggles-expanded"
				)}
				// key={cell.id}
				style={{ width: props.column.getSize() }}
				role="row"
				tabIndex={-1}
				onClick={(e) => {
					// if (rowSelectTrigger === "onClickToggle") {
					// 	return;
					// }

					if (!(e.target instanceof HTMLElement) || e.target.tagName === "BUTTON") {
						return;
					}

					// if (rowSelectBehavior === "multiselect") {
					// 	cell.row.toggleSelected();
					// } else if (rowSelectBehavior === "select") {
					// 	table.toggleAllRowsSelected(false);
					// 	cell.row.toggleSelected();
					// }
				}}
				onFocus={() => {
					cellValueRef.current?.focus();
				}}
				onKeyDown={(e) => {
					// Prevent the event from bubbling up to the table
					e.stopPropagation();
				}}
			>
				{/* {cellIndex === 0 && (
				<RowSelector
					table={table}
					rowSelectBehavior={rowSelectBehavior}
					rowSelectTrigger={rowSelectTrigger}
					cell={cell}
				/>
			)}

			{columnDef.copyable && <CopyCellValueButton cell={cell} />} */}

				{typeMismatchError ? (
					<BasicTooltip
						triggerProps={{ asChild: true }}
						content="Type conversion error: cannot convert to selected type"
					>
						{renderWrapper}
					</BasicTooltip>
				) : (
					renderWrapper
				)}
			</div>
			{typeMismatchError && (
				<div
					className="absolute top-0 right-0 w-0 h-0 border-t-[6px] border-t-palette-negative-9 border-r-[6px] border-r-palette-negative-9 border-l-[6px] border-l-transparent border-b-[6px] border-b-transparent"
					title="Type mismatch error"
				/>
			)}
		</div>
	);

	return (
		<MaybeWrapInColumnReadonlyPopover
			key={props.cell.id}
			columnType={columnDef.fieldType}
			columnReadonly={props.table.options.meta?.tableEditable === false || columnDef.readonly}
			renderAsRowDetail={false}
			value={rendered}
		/>
	);
};

const safeStringify = (o: unknown) => {
	try {
		return JSON.stringify(o);
	} catch (e) {
		return String(o);
	}
};
